import React, { ReactNode } from "react";
import {
	Box,
	Button,
	Typography,
	Card,
	CardContent,
	CardHeader,
	Divider,
	Paper
} from "@mui/material";
import { useTranslation } from "react-i18next";

// Constants to avoid magic numbers
const DEFAULT_ICON_SIZE = { width: 120, height: 120 };
const DEFAULT_CONTAINER_MIN_HEIGHT = "60vh";
const DEFAULT_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const DEFAULT_SHADOW = "0 10px 30px rgba(102, 126, 234, 0.3)";
const CARD_MIN_HEIGHT = 200;
const STANDARD_MIN_HEIGHT = 400;
const CARD_MAX_WIDTH = 400;
const STANDARD_MAX_WIDTH = 600;

// Color constants
const TITLE_COLOR = "#4a5568";
const MESSAGE_COLOR = "#666666";

// Base props shared between components
interface BaseEmptyStateProps {
	icon: ReactNode;
	title: string;
	message: string;
	iconBackgroundGradient?: string;
	iconBoxShadow?: string;
	onRetry?: () => void;
	retryButtonText?: string;
	iconSize?: {
		width: number;
		height: number;
	};
	titleVariant?: "h4" | "h5" | "h6" | "subtitle1" | "body1";
	messageVariant?: "body1" | "body2";
}

// Standard empty state props
interface EmptyStateProps extends BaseEmptyStateProps {
	containerMinHeight?: string;
	containerSx?: Record<string, unknown>;
}

// Card empty state props
interface CardEmptyStateProps extends BaseEmptyStateProps {
	cardTitle?: string;
	cardSx?: Record<string, unknown>;
	hideIcon?: boolean;
}

// Shared content component
interface EmptyStateContentProps extends BaseEmptyStateProps {
	maxWidth: number;
	buttonSize: "small" | "medium";
	buttonBorderRadius: number;
	buttonPaddingX: number;
	buttonPaddingY: number;
	iconMarginBottom: number;
	titleMarginBottom?: number;
	messageMarginBottom: number;
	hideIcon?: boolean;
}

const EmptyStateContent: React.FC<EmptyStateContentProps> = ({
	icon,
	title,
	message,
	iconBackgroundGradient = DEFAULT_GRADIENT,
	iconBoxShadow = DEFAULT_SHADOW,
	onRetry,
	retryButtonText,
	iconSize = DEFAULT_ICON_SIZE,
	titleVariant = "h4",
	messageVariant = "body1",
	maxWidth,
	buttonSize,
	buttonBorderRadius,
	buttonPaddingX,
	buttonPaddingY,
	iconMarginBottom,
	titleMarginBottom,
	messageMarginBottom,
	hideIcon = false
}) => {
	const { t } = useTranslation();

	return (
		<>
			{!hideIcon && (
				<Box
					sx={{
						width: iconSize.width,
						height: iconSize.height,
						borderRadius: "50%",
						background: iconBackgroundGradient,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						mb: iconMarginBottom,
						boxShadow: iconBoxShadow
					}}
				>
					{icon}
				</Box>
			)}
			<Typography
				variant={titleVariant}
				component="h3"
				gutterBottom
				sx={{
					fontWeight: 600,
					color: TITLE_COLOR,
					textAlign: "center",
					mb: titleMarginBottom
				}}
			>
				{title}
			</Typography>
			<Typography
				variant={messageVariant}
				sx={{
					color: MESSAGE_COLOR,
					textAlign: "center",
					maxWidth,
					lineHeight: 1.6,
					mb: messageMarginBottom
				}}
			>
				{message}
			</Typography>
			{onRetry && (
				<Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
					<Button
						disableRipple
						variant="outlined"
						color="secondary"
						onClick={onRetry}
						size={buttonSize}
						sx={{
							borderRadius: buttonBorderRadius,
							px: buttonPaddingX,
							py: buttonPaddingY,
							textTransform: "none",
							fontWeight: 500
						}}
					>
						{retryButtonText || t("common.retry")}
					</Button>
				</Box>
			)}
		</>
	);
};

// Standard EmptyState component
const EmptyState: React.FC<EmptyStateProps> = ({
	icon,
	title,
	message,
	iconBackgroundGradient,
	iconBoxShadow,
	onRetry,
	retryButtonText,
	iconSize,
	titleVariant,
	messageVariant,
	containerMinHeight = DEFAULT_CONTAINER_MIN_HEIGHT,
	containerSx = {}
}) => {
	const titleMarginBottom = onRetry ? undefined : 2;
	const messageMarginBottom = onRetry ? 3 : 4;

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="center"
			minHeight={containerMinHeight}
			width="100%"
		>
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				px={4}
				sx={{
					borderRadius: 4,
					minHeight: STANDARD_MIN_HEIGHT,
					maxWidth: STANDARD_MAX_WIDTH,
					width: "100%",
					...containerSx
				}}
			>
				<EmptyStateContent
					icon={icon}
					title={title}
					message={message}
					iconBackgroundGradient={iconBackgroundGradient}
					iconBoxShadow={iconBoxShadow}
					onRetry={onRetry}
					retryButtonText={retryButtonText}
					iconSize={iconSize}
					titleVariant={titleVariant}
					messageVariant={messageVariant}
					maxWidth={STANDARD_MAX_WIDTH}
					buttonSize="medium"
					buttonBorderRadius={3}
					buttonPaddingX={3}
					buttonPaddingY={1}
					iconMarginBottom={3}
					titleMarginBottom={titleMarginBottom}
					messageMarginBottom={messageMarginBottom}
				/>
			</Box>
		</Box>
	);
};

// Card-wrapped EmptyState component
const CardEmptyState: React.FC<CardEmptyStateProps> = ({
	icon,
	title,
	message,
	iconBackgroundGradient,
	iconBoxShadow,
	onRetry,
	retryButtonText,
	iconSize,
	titleVariant,
	messageVariant,
	cardTitle,
	cardSx = {},
	hideIcon = false
}) => {
	const titleMarginBottom = onRetry ? undefined : 1;
	const messageMarginBottom = onRetry ? 2 : 0;

	return (
		<Card
			component={Paper}
			elevation={0}
			sx={{
				margin: "16px",
				borderRadius: "8px",
				...cardSx
			}}
		>
			{cardTitle && (
				<>
					<CardHeader title={<Typography variant="h6">{cardTitle}</Typography>} />
					<Divider />
				</>
			)}
			<CardContent>
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					py={hideIcon ? 0 : 3}
					px={4}
					sx={{
						borderRadius: 0,
						minHeight: hideIcon ? 0 : CARD_MIN_HEIGHT,
						maxWidth: "100%",
						width: "100%"
					}}
				>
					<EmptyStateContent
						icon={icon}
						title={title}
						message={message}
						iconBackgroundGradient={iconBackgroundGradient}
						iconBoxShadow={iconBoxShadow}
						onRetry={onRetry}
						retryButtonText={retryButtonText}
						iconSize={iconSize}
						titleVariant={titleVariant}
						messageVariant={messageVariant}
						maxWidth={CARD_MAX_WIDTH}
						buttonSize="small"
						buttonBorderRadius={2}
						buttonPaddingX={2}
						buttonPaddingY={0.5}
						iconMarginBottom={2}
						titleMarginBottom={titleMarginBottom}
						messageMarginBottom={messageMarginBottom}
						hideIcon={hideIcon}
					/>
				</Box>
			</CardContent>
		</Card>
	);
};

export default EmptyState;
export { CardEmptyState };
